import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

/**
 * Onboarding Flow
 * 
 * 3 screens max:
 * 1. Welcome - What this app does for you
 * 2. The Three Pillars - Quick visual overview
 * 3. First Action - Start immediately
 */

const OnboardingPage = ({ onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const completeOnboarding = async () => {
    try {
      await fetch(`${API_URL}/api/me/complete-onboarding`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Failed to mark onboarding complete:', e);
    }
    
    if (onComplete) {
      onComplete();
    } else {
      navigate('/client/hub');
    }
  };

  const screens = [
    // Screen 1: Welcome
    {
      render: () => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <div className="w-20 h-20 bg-burnt-terra/10 rounded-full flex items-center justify-center mb-8">
            <span className="text-4xl">🎯</span>
          </div>
          
          <h1 className="font-display text-3xl md:text-4xl font-bold text-obsidian-night mb-4">
            Calibrate Your Presence
          </h1>
          
          <p className="text-lg text-shadow-pine/80 max-w-md mb-8 leading-relaxed">
            GNTL STRNGTH helps you develop authentic presence through three practices: 
            movement, voice, and reflection.
          </p>
          
          <p className="text-shadow-pine/60 text-sm max-w-sm">
            No dashboards. No gamification. Just focused practice.
          </p>
        </div>
      )
    },
    
    // Screen 2: The Three Pillars (Visual)
    {
      render: () => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
          <h2 className="font-display text-2xl font-bold text-obsidian-night mb-8 text-center">
            Three Pillars of Presence
          </h2>
          
          <div className="grid gap-4 w-full max-w-md">
            {/* Physical */}
            <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-burnt-terra/20">
              <div className="w-12 h-12 bg-burnt-terra/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-burnt-terra" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="5" r="2"/>
                  <path d="M12 7v6M12 13l-3 6M12 13l3 6M12 10l-4 2M12 10l4 2"/>
                </svg>
              </div>
              <div>
                <p className="font-bold text-obsidian-night">Physical Architecture</p>
                <p className="text-sm text-shadow-pine/70">Train your body as a container for presence</p>
              </div>
            </div>
            
            {/* Voice */}
            <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-burnt-terra/20">
              <div className="w-12 h-12 bg-shadow-pine/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-shadow-pine" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="2" width="6" height="10" rx="3"/>
                  <path d="M5 10v1a7 7 0 0014 0v-1M12 18v4M8 22h8"/>
                </svg>
              </div>
              <div>
                <p className="font-bold text-obsidian-night">Voice Integration</p>
                <p className="text-sm text-shadow-pine/70">Calibrate how you speak and listen</p>
              </div>
            </div>
            
            {/* Writing */}
            <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-burnt-terra/20">
              <div className="w-12 h-12 bg-obsidian-night/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-obsidian-night" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                  <path d="M2 2l7.586 7.586"/>
                  <circle cx="11" cy="11" r="2"/>
                </svg>
              </div>
              <div>
                <p className="font-bold text-obsidian-night">Excavation Writing</p>
                <p className="text-sm text-shadow-pine/70">Uncover the stories that shape you</p>
              </div>
            </div>
          </div>
          
          <p className="text-center text-shadow-pine/60 text-sm mt-6 max-w-sm">
            Explore any pillar in any order. Start where you feel drawn.
          </p>
        </div>
      )
    },
    
    // Screen 3: First Action
    {
      render: () => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <div className="w-16 h-16 bg-shadow-pine/20 rounded-full flex items-center justify-center mb-6">
            <Check className="w-8 h-8 text-shadow-pine" />
          </div>
          
          <h2 className="font-display text-2xl font-bold text-obsidian-night mb-4">
            Ready to Begin
          </h2>
          
          <p className="text-shadow-pine/80 max-w-md mb-8">
            Your hub is waiting. Choose a pillar and take your first step.
          </p>
          
          <div className="bg-ivory-mist rounded-xl p-4 max-w-sm w-full mb-6">
            <p className="text-sm text-shadow-pine/70 mb-2">Quick tip:</p>
            <p className="text-sm text-obsidian-night">
              Consistency matters more than duration. Even 5 minutes of focused practice builds presence.
            </p>
          </div>
        </div>
      )
    }
  ];

  const isLastStep = step === screens.length - 1;

  return (
    <div className="min-h-screen bg-ivory-mist flex flex-col">
      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-8">
        {screens.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === step ? 'bg-burnt-terra' : i < step ? 'bg-burnt-terra/50' : 'bg-burnt-terra/20'
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center">
        {screens[step].render()}
      </div>

      {/* Navigation */}
      <div className="p-6 flex justify-between items-center max-w-md mx-auto w-full">
        {step > 0 ? (
          <Button
            variant="ghost"
            onClick={() => setStep(step - 1)}
            className="text-shadow-pine"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        ) : (
          <div />
        )}

        {isLastStep ? (
          <Button
            onClick={completeOnboarding}
            className="bg-burnt-terra hover:bg-burnt-terra/90 text-white px-8"
          >
            Enter Hub
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={() => setStep(step + 1)}
            className="bg-burnt-terra hover:bg-burnt-terra/90 text-white"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Skip option */}
      {!isLastStep && (
        <div className="pb-6 text-center">
          <button
            onClick={completeOnboarding}
            className="text-sm text-shadow-pine/50 hover:text-shadow-pine"
          >
            Skip introduction
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;
